import React, { useEffect, useState, useRef } from "react";
import { getTodayBookings } from "../api";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import "../styles/Board2.css";
import LiveClock from "../components/LiveClock";
import FlipCell from "../components/FlipCell";

/**
 * ArrivalsDeparturesBoard
 * - Fetches bookings via getTodayBookings()
 * - Refreshes every 60s (and shows countdown)
 * - Displays arrivals on left (booking_type 'in'), departures on right (booking_type 'out')
 * - Rows are sorted newest-first (top)
 * - Time column is narrower; added Type column after Registration
 */

const useInterval = (callback, delay) => {
  const savedRef = useRef();
  useEffect(() => { savedRef.current = callback; }, [callback]);
  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => savedRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
};

const Board2 = () => {
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(60);

  // store last loaded raw arrays to detect changes and keep newest-first
  const fetchBookings = async () => {
    try {
      const bookings = await getTodayBookings();

      // Sort by date+time descending so newest on top
      const sortDesc = (a, b) =>
        new Date(b.date + " " + b.time) - new Date(a.date + " " + a.time);

      const inBookings = bookings
        .filter((b) => b.booking_type && b.booking_type.toLowerCase() === "in")
        .sort(sortDesc);

      const outBookings = bookings
        .filter((b) => b.booking_type && b.booking_type.toLowerCase() === "out")
        .sort(sortDesc);

      setArrivals(inBookings);
      setDepartures(outBookings);
      setSecondsLeft(60);
    } catch (err) {
      console.error("Error loading bookings:", err);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh every 60 seconds
  useInterval(fetchBookings, 60000);

  // countdown for UI
  useInterval(() => {
    setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
  }, 1000);

  const renderHeader = (title, icon) => (
    <div className="board-section">
      <div className="board-header">
        <div className="board-title">
          {icon}
          <div className="board-title-text">{title}</div>
        </div>

      </div>
    </div>
  );

/* --- Inside Board2.js --- */

const renderRows = (bookings) => {
  return (
    <div className="board-table">
      {/* STEP 1: ADD THE MISSING HEADER ROW HERE
        Use the 'header' class for styling and the same cell classes 
        to ensure columns line up with data rows.
      */}
      <div className="board-row header">
        <div className="cell time">Time</div>
        <div className="cell dest">Destination</div>
        <div className="cell reg">Registration</div> 
        <div className="cell type">Type</div>
      </div>

      {/* RENDER DATA ROWS */}
{bookings.map((b, i) => {
  const time = (b.time || "-----").slice(0, 5);                // HH:MM
  const dest = (b.location || "UNKNOWN").padEnd(10).slice(0,10);
  const reg  = (b.registration || "G-????").padEnd(7).slice(0,7);
  const type = (b.aircraft_type || "-").padEnd(10).slice(0,10);

  return (
    <div className="board-row data-row" key={`${b.id ?? i}-${reg}-${time}`}>
      <div className="cell time">
        <FlipCell value={time} width={5} />
      </div>

      <div className="cell dest">
        <FlipCell value={dest} width={10} />
      </div>

      <div className="cell reg">
        <FlipCell value={reg} width={7} />
      </div>

      <div className="cell type">
        <FlipCell value={type} width={10} />
      </div>
    </div>
  );
})}
    </div>
  );
};

  // Whole layout: two columns — left ARRIVALS, right DEPARTURES
return (
  <div className="arrivals-departures-page">

    {/* NEW HEADER LAYOUT */}
    <div className="board2-topbar">
      
      {/* LEFT: Subtle Radar + Countdown */}
      <div className="refresh-container">
        <div className="radar-scope">
          <div className="radar-sweep" style={{ animationDuration: '2s' }}></div>
        </div>
        <span>REFRESH IN {secondsLeft}s</span>
      </div>

      {/* CENTER: Title */}
      <div className="board-main-title">
        Popham Airfield
      </div>

      {/* RIGHT: Live Clock */}
      <div className="topbar-right">
        <LiveClock showSeconds={true} />
      </div>
    </div>

    {/* REST OF BOARD REMAINS THE SAME */}
    <div className="boards-wrapper">
      <div className="board-column">
        {renderHeader("ARRIVALS", <FlightLandIcon className="big-icon" />)}
        <div className="board-table">{renderRows(arrivals)}</div>
      </div>

      <div className="board-column">
        {renderHeader("DEPARTURES", <FlightTakeoffIcon className="big-icon" />)}
        <div className="board-table">{renderRows(departures)}</div>
      </div>
    </div>
  </div>
);
};

export default Board2;
